import axios from 'axios'
import { Message } from 'element-ui'
import Cookies from 'js-cookie'
import router from '@/router'
import { removeRouterList } from '@/utils/permission'

const TOKEN_KEY         = 'charge_token'
const TOKEN_TYPE_KEY    = 'charge_token_type'
const REFRESH_TOKEN_KEY = 'charge_refresh_token'

/** 读取 accessToken */
export const getToken = () => Cookies.get(TOKEN_KEY)

/** 读取 tokenType（默认 Bearer） */
export const getTokenType = () => Cookies.get(TOKEN_TYPE_KEY) || 'Bearer'

/** 读取 refreshToken */
export const getRefreshToken = () => Cookies.get(REFRESH_TOKEN_KEY)

/**
 * 登录成功后统一保存 token 信息
 * @param {{ accessToken: string, refreshToken?: string, tokenType?: string }} loginData
 */
export const saveToken = (loginData) => {
  Cookies.set(TOKEN_KEY,      loginData.accessToken,           { sameSite: 'Lax' })
  Cookies.set(TOKEN_TYPE_KEY, loginData.tokenType || 'Bearer', { sameSite: 'Lax' })
  if (loginData.refreshToken) {
    Cookies.set(REFRESH_TOKEN_KEY, loginData.refreshToken, { sameSite: 'Lax' })
  }
}

/** 清除所有 token 相关 cookie */
export const removeToken = () => {
  Cookies.remove(TOKEN_KEY)
  Cookies.remove(TOKEN_TYPE_KEY)
  Cookies.remove(REFRESH_TOKEN_KEY)
}

/** @deprecated 请使用 saveToken(loginData) 代替 */
export const setToken = (token) => Cookies.set(TOKEN_KEY, token, { sameSite: 'Lax' })

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
})

/**
 * 在途请求池：每个请求注册自己的 AbortController，
 * 强制登出时统一 abort 掉所有在途请求，避免重复弹窗。
 */
const pendingControllers = new Set()

/** 强制登出锁（refreshToken 也失效时） */
let isRedirecting = false

/** 正在刷新 token 的 Promise，用于并发 401 时排队等待同一次 refresh */
let refreshingPromise = null

/** 统一执行强制登出（refreshToken 失效或不存在） */
function handleUnauthorized() {
  if (isRedirecting) return
  isRedirecting = true

  pendingControllers.forEach(ctrl => ctrl.abort())
  pendingControllers.clear()

  Message.error('登录已过期，请重新登录')
  import('@/store').then(({ default: store }) => {
    store.commit('RESET_USER')
    removeToken()
    removeRouterList()
    router.replace('/login').finally(() => {
      isRedirecting = false
    })
  })
}

/**
 * 用 refreshToken 换新 accessToken。
 * 并发 401 时只发一次 refresh 请求，其他请求排队等结果后重放。
 */
function tryRefresh(failedConfig) {
  if (!refreshingPromise) {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      handleUnauthorized()
      return Promise.reject(new Error('no refresh token'))
    }
    // 用原生 axios 避免走自身拦截器造成循环
    refreshingPromise = axios.post(
      process.env.VUE_APP_BASE_API + '/auth/refresh',
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    ).then(res => {
      const data = res.data?.data || res.data
      if (!data?.accessToken) throw new Error('refresh failed')
      saveToken(data)
      return data.accessToken
    }).catch(err => {
      handleUnauthorized()
      return Promise.reject(err)
    }).finally(() => {
      refreshingPromise = null
    })
  }

  return refreshingPromise.then(newAccessToken => {
    // 用新 token 重放原请求
    failedConfig.headers['Authorization'] = `${getTokenType()} ${newAccessToken}`
    // 已被 abort 的 controller 不能复用，创建新的
    const ctrl = new AbortController()
    pendingControllers.add(ctrl)
    failedConfig.signal = ctrl.signal
    failedConfig._ctrl  = ctrl
    return service(failedConfig)
  })
}

/* ── 请求拦截：注入 token，并将每个请求的 AbortController 加入池 ── */
service.interceptors.request.use(
  config => {
    // 已在跳转中：直接 abort，不再发出新请求
    if (isRedirecting) {
      const ctrl = new AbortController()
      ctrl.abort()
      config.signal = ctrl.signal
      return config
    }

    // 为每个正常请求创建独立的 AbortController 并注册到池中
    const ctrl = new AbortController()
    pendingControllers.add(ctrl)
    config.signal = ctrl.signal
    config._ctrl = ctrl   // 存到 config 方便响应时移除

    const token = getToken()
    if (token) {
      config.headers['Authorization'] = `${getTokenType()} ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

/* ── 响应拦截：统一错误处理 + 401 跳登录 ────────────────── */
service.interceptors.response.use(
  response => {
    // 请求完成，从池中移除
    if (response.config._ctrl) {
      pendingControllers.delete(response.config._ctrl)
    }

    // 已在跳转中（被其他 401 触发），静默丢弃
    if (isRedirecting) return Promise.reject(new Error('unauthorized'))

    // blob 下载直接返回原始数据
    if (response.config.responseType === 'blob') {
      return response.data
    }

    const res = response.data
    // 业务码 401：先尝试无感刷新，失败才踢出
    if (res.code === 401) {
      return tryRefresh(response.config)
    }
    if (res.code !== undefined && res.code !== 200) {
      Message.error(res.message || '请求失败')
      return Promise.reject(new Error(res.message || '请求失败'))
    }
    return res
  },
  error => {
    // 请求完成（异常），从池中移除
    if (error.config?._ctrl) {
      pendingControllers.delete(error.config._ctrl)
    }

    // 被 abort（含 handleUnauthorized 主动取消的），静默忽略
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }

    // 已在跳转中，静默忽略
    if (isRedirecting) return Promise.reject(error)

    // HTTP 401：先尝试无感刷新
    const status = error.response?.status
    if (status === 401) {
      return tryRefresh(error.config)
    }

    const msg = error.response?.data?.message || error.message || '网络异常，请稍后重试'
    Message.error(msg)
    return Promise.reject(error)
  }
)

export default service

export const get = (url, params) => service.get(url, { params })
export const post = (url, data) => service.post(url, data)

/**
 * 文件下载：POST 请求，responseType=blob，自动触发浏览器下载
 * @param {string} url
 * @param {object} data 请求体
 * @param {string} filename 默认文件名（服务端 Content-Disposition 优先）
 */
export const downloadPost = (url, data, filename = 'export.xlsx') => {
  return service.post(url, data, { responseType: 'blob' }).then(blob => {
    triggerDownload(blob, filename)
  })
}

/**
 * 文件下载：GET 请求，responseType=blob，自动触发浏览器下载
 * @param {string} url
 * @param {string} filename 默认文件名
 */
export const downloadGet = (url, filename = 'export') => {
  return service.get(url, { responseType: 'blob' }).then(blob => {
    triggerDownload(blob, filename)
  })
}

function triggerDownload(blob, filename) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(a.href)
}
